import { Ray } from '@babylonjs/core/Culling/ray';
import { Matrix, Plane, Quaternion, Vector3 } from '@babylonjs/core/Maths/math';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Scene } from '@babylonjs/core/scene';
import { DeepImmutable } from '@babylonjs/core/types';
import { SweepFeature } from '@kb3d/models/classes/features/sweep-feature';
import { WorkerProcessorHost } from '@kb3d/models/interfaces/worker-types';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';
import { rotationToVector } from '@kb3d/view/helpers/math-helper';
import { CreateMiteredRibbon } from '@kb3d/view/modules/miteredRibbonBuilder';
import { parseSketchGeometry } from '@kb3d/view/modules/sketching/util';
import { FeatureGeometryProcessor, ProcessorData } from './base-processor';
import { applyExtrusion, ExtrudeGeometryDependencyData, ExtrudeGeometryWorkerPayload } from './extrude-geometry';

// This is passed to the worker as additional information needed
export type SweepGeometryWorkerPayload = ExtrudeGeometryWorkerPayload & {
    pathPoints: Float32Array; // array of vector 3 values
    pathMeshTransform: DeepImmutable<Float32Array | number[]>;
    thisMeshTransform: DeepImmutable<Float32Array | number[]>;
};

// This is processed and deserialized in the worker based on the worker payload
export type SweepGeometryDependencyData = ExtrudeGeometryDependencyData & {
    pathPoints: Vector3[];
    pathMeshTransform: Matrix;
    thisMeshTransform: Matrix;
};

interface ExtrudeOptions {
    shape: Vector3[];
    path: Vector3[];
    updatable: boolean;
    cap: number;
    closeShape: boolean;
}

export class SweepGeometryProcessor extends FeatureGeometryProcessor<SweepFeature, SweepGeometryDependencyData> {
    readWorkerPayload(_: WorkerProcessorHost, data: SweepGeometryWorkerPayload) {
        if (!data) {
            return null;
        }

        const pathMeshTransform = Matrix.FromArray(data.pathMeshTransform);
        const thisMeshTransform = Matrix.FromArray(data.thisMeshTransform);
        const pathPoints: Vector3[] = [];
        for (let i = 0; i < data.pathPoints.length / 3; i++) {
            pathPoints[i] = new Vector3(data.pathPoints[i * 3], data.pathPoints[i * 3 + 1], data.pathPoints[i * 3 + 2]);
        }
        const joinGeometryDependencyData: SweepGeometryDependencyData = {
            sketchNormal: new Vector3(data.sketchNormal.x ?? 0, data.sketchNormal.y ?? 0, data.sketchNormal.z ?? 0),
            pathPoints,
            pathMeshTransform,
            thisMeshTransform,
        };
        return joinGeometryDependencyData;
    }

    updateMeshGeometry(
        feature: SweepFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        dependencyData: ProcessorData<SweepGeometryDependencyData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges, dependencyData);

        if (!dependencyData.data) {
            return newVertexData;
        }

        const { pathPoints, pathMeshTransform, thisMeshTransform } = dependencyData.data;

        if (!newVertexData.positions || newVertexData.positions.length === 0 || pathPoints.length === 0) {
            return newVertexData;
        }

        const { segments } = parseSketchGeometry(newVertexData);
        const extrudedMeshes: Mesh[] = [];

        for (let i = 0; i < segments.length; i++) {
            const shape = segments[i];

            // Transform the path and the profile sketch into world space so we can combine them without worrying about local transforms
            const pathTransform = pathMeshTransform;
            const sketchTransform = thisMeshTransform;
            for (let i = 0; i < pathPoints.length; i++) {
                Vector3.TransformCoordinatesToRef(pathPoints[i], pathTransform, pathPoints[i]);
            }
            for (let i = 0; i < shape.length; i++) {
                Vector3.TransformCoordinatesToRef(shape[i], sketchTransform, shape[i]);
            }

            // Determine the vector to use as the primary direction of the extrusion
            const pathPrimaryVector = Vector3.Zero();
            let j = 0;
            while (pathPrimaryVector.length() === 0) {
                j++;
                pathPrimaryVector.copyFrom(pathPoints[j]).subtractInPlace(pathPoints[0]);
            }

            // Calculate the matrix to move the sketch path primary direction to be perpindicular to the XY plane for consistent behavior
            const rotToZ = rotationToVector(new Vector3(0, 0, 1), pathPrimaryVector);
            const pathTransformToZ = new Matrix();
            const pathTransformToZInv = new Matrix();
            rotToZ.toRotationMatrix(pathTransformToZ);
            Matrix.Compose(Vector3.One(), Quaternion.Identity(), pathPoints[0].scale(-1)).multiplyToRef(
                pathTransformToZ,
                pathTransformToZ
            );
            pathTransformToZ.invertToRef(pathTransformToZInv);

            // Transform both the path and the extrusion shape with the same matrix so the
            // shape and the path are positioned the same as before in relation to each other
            // but oriented to be extruded out of the XY plane, in the Z direction
            for (let i = 0; i < pathPoints.length; i++) {
                Vector3.TransformCoordinatesToRef(pathPoints[i], pathTransformToZ, pathPoints[i]);
            }
            for (let i = 0; i < shape.length; i++) {
                Vector3.TransformCoordinatesToRef(shape[i], pathTransformToZ, shape[i]);
                // Project the extrude shape onto the XY plane so that it's perfecly perpindicular to the sweep path
                shape[i].set(shape[i].x, shape[i].y, 0);
            }

            const extrudeOptions: ExtrudeOptions = {
                shape,
                path: pathPoints,
                updatable: false,
                cap: Mesh.NO_CAP,
                closeShape: false,
                // firstNormal: sketchNormal, this property is added in a future version of babylon
            };

            if (feature.capEnd && feature.capStart) {
                extrudeOptions.cap = Mesh.CAP_ALL;
                extrudeOptions.closeShape = true;
            } else if (feature.capStart) {
                extrudeOptions.cap = Mesh.CAP_START;
                extrudeOptions.closeShape = true;
            } else if (feature.capEnd) {
                extrudeOptions.cap = Mesh.CAP_END;
                extrudeOptions.closeShape = true;
            }

            // Perform the extrusion of the shape defined along the XY plane, to the path oriented to coincide with the Z+ axis
            let extruded: Mesh;
            if (feature.mitredExtrusion) {
                extrudeOptions.closeShape = false;
                extruded = this.mitredExtrude(feature._dynamicId, extrudeOptions, this.scene);
            } else {
                extruded = MeshBuilder.ExtrudeShape(feature._dynamicId, extrudeOptions, this.scene);
            }

            // Rotate the resulting extruded shape to match the orientation of the original shape profile
            sketchTransform.invert();
            pathTransformToZInv.multiplyToRef(sketchTransform, pathTransformToZInv);
            extruded.bakeTransformIntoVertices(pathTransformToZInv);

            extrudedMeshes.push(extruded);
        }

        const extrudedAll = Mesh.MergeMeshes(extrudedMeshes, true);
        if (extrudedAll) {
            applyExtrusion(newVertexData, extrudedAll, false);
            extrudedAll.dispose();
        }

        return newVertexData;
    }

    mitredExtrude(featureId: string, options: ExtrudeOptions, scene: Scene) {
        const shape = options.shape;
        const path = options.path;
        const closed = options.closeShape || false;

        const nbPoints = path.length;
        const line = Vector3.Zero();
        const nextLine = Vector3.Zero();
        let axisX = Vector3.Zero();
        let axisY = Vector3.Zero();
        let axisZ = Vector3.Zero();
        let nextAxisX = Vector3.Zero();
        let nextAxisY = Vector3.Zero();
        let nextAxisZ = Vector3.Zero();
        let startPoint = Vector3.Zero();
        const nextStartPoint = Vector3.Zero();
        const bisector = Vector3.Zero();
        let distance = 0;
        let ray;

        const allPaths = [];
        // Note that the sketch will always be rotated such that the profile is in the XY plane
        const up = Vector3.Up();

        for (let s = 0; s < shape.length; s++) {
            path[1].subtractToRef(path[0], line);
            axisZ = line.clone().normalize();
            axisX = Vector3.Cross(Vector3.Up(), axisZ).normalize();
            axisY = Vector3.Cross(axisZ, axisX);
            startPoint = path[0].add(axisX.scale(shape[s].x)).add(axisY.scale(shape[s].y));
            const ribbonPath = [startPoint.clone()];
            for (let p = 0; p < nbPoints - 2; p++) {
                path[p + 2].subtractToRef(path[p + 1], nextLine);
                nextAxisZ = nextLine.clone().normalize();
                nextAxisX = Vector3.Cross(up, nextAxisZ).normalize();
                nextAxisY = Vector3.Cross(nextAxisZ, nextAxisX);
                nextAxisZ.subtractToRef(axisZ, bisector);
                const planeParallel = Vector3.Cross(nextAxisZ, axisZ);
                const planeNormal = Vector3.Cross(planeParallel, bisector);
                const plane = Plane.FromPositionAndNormal(path[p + 1], planeNormal);
                ray = new Ray(startPoint, axisZ);
                distance = ray.intersectsPlane(plane) || 0;
                startPoint.addToRef(axisZ.scale(distance), nextStartPoint);
                ribbonPath.push(nextStartPoint.clone());

                axisX = nextAxisX.clone();
                axisY = nextAxisY.clone();
                axisZ = nextAxisZ.clone();
                startPoint = nextStartPoint.clone();
            }
            // Last Point
            if (closed) {
                path[0].subtractToRef(path[nbPoints - 1], nextLine);
                nextAxisZ = nextLine.clone().normalize();
                nextAxisX = Vector3.Cross(up, nextAxisZ).normalize();
                nextAxisY = Vector3.Cross(nextAxisZ, nextAxisX);
                nextAxisZ.subtractToRef(axisZ, bisector);
                const closedPlaneParallel = Vector3.Cross(nextAxisZ, axisZ);
                const closedPlaneNormal = Vector3.Cross(closedPlaneParallel, bisector);
                const closedPlane = Plane.FromPositionAndNormal(path[nbPoints - 1], closedPlaneNormal);
                ray = new Ray(startPoint, axisZ);
                distance = ray.intersectsPlane(closedPlane) || 0;
                startPoint.addToRef(axisZ.scale(distance), nextStartPoint);
                ribbonPath.push(nextStartPoint.clone());

                axisX = nextAxisX.clone();
                axisY = nextAxisY.clone();
                axisZ = nextAxisZ.clone();
                startPoint = nextStartPoint.clone();

                path[1].subtractToRef(path[0], nextLine);
                nextAxisZ = nextLine.clone().normalize();
                nextAxisX = Vector3.Cross(up, nextAxisZ).normalize();
                nextAxisY = Vector3.Cross(nextAxisZ, nextAxisX);
                nextAxisZ.subtractToRef(axisZ, bisector);
                const planeParallel = Vector3.Cross(nextAxisZ, axisZ);
                const planeNormal = Vector3.Cross(planeParallel, bisector);
                const plane = Plane.FromPositionAndNormal(path[0], planeNormal);
                ray = new Ray(startPoint, axisZ);
                distance = ray.intersectsPlane(plane) || 0;
                startPoint.addToRef(axisZ.scale(distance), nextStartPoint);
                ribbonPath.shift();
                ribbonPath.unshift(nextStartPoint.clone());
            } else {
                const planeNormal = axisZ;
                const plane = Plane.FromPositionAndNormal(path[nbPoints - 1], planeNormal);
                ray = new Ray(startPoint, axisZ);
                const distance = ray.intersectsPlane(plane) || 0;
                startPoint.addToRef(axisZ.scale(distance), nextStartPoint);
                ribbonPath.push(nextStartPoint.clone());
            }
            allPaths.push(ribbonPath);
        }

        const ribbon = CreateMiteredRibbon(
            featureId,
            {
                pathArray: allPaths,
                sideOrientation: Mesh.DOUBLESIDE,
                closeArray: false,
                closePath: false,
                cap: options.cap,
            },
            scene
        );

        return ribbon;
    }
}
